import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "—";

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export function useResource(resource, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/app-admin/data/${resource}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [resource]);
  useEffect(() => { load(); }, [load, ...deps]);
  return { data, loading, reload: load };
}

export function HubRow({ icon: Icon, title, subtitle, onClick, badge, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="flex w-full items-center gap-3.5 rounded-2xl border border-black/5 bg-white p-4 text-left transition-shadow hover:shadow-md">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-terracotta/10 text-terracotta">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-bold text-charcoal">{title}</span>
        {subtitle && <span className="mt-0.5 block text-[13px] leading-snug text-charcoal/55">{subtitle}</span>}
      </span>
      {badge != null && badge > 0 && (
        <span className="rounded-full bg-terracotta px-2.5 py-1 text-xs font-bold text-white">{badge}</span>
      )}
      <ChevronRight className="h-4 w-4 shrink-0 text-charcoal/30" />
    </button>
  );
}

export function SubHeader({ title, subtitle, onBack }) {
  return (
    <div className="mb-5">
      <div className="relative flex items-center justify-center py-1">
        <button onClick={onBack} data-testid="back-btn" aria-label="Back"
          className="absolute left-0 grid h-9 w-9 place-items-center rounded-full bg-charcoal/5 text-charcoal transition-colors hover:bg-charcoal/10">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="font-display text-lg font-bold text-charcoal">{title}</h2>
      </div>
      {subtitle && <p className="mt-1 text-center text-[13px] text-charcoal/55">{subtitle}</p>}
    </div>
  );
}

export function StatTile({ n, label, active, warn, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`rounded-xl border p-3.5 text-left transition-colors ${active ? "border-terracotta bg-terracotta/5" : "border-black/5 bg-cream/60"} ${warn ? "border-terracotta" : ""}`}>
      <p className={`font-display text-2xl font-bold ${active || warn ? "text-terracotta" : "text-charcoal"}`}>{n}</p>
      <p className="mt-0.5 text-xs font-semibold text-charcoal/55">{label}</p>
    </button>
  );
}

export function PillTabs({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="pill-tabs">
      {options.map((o) => (
        <button key={o.key} onClick={() => onChange(o.key)} data-testid={`pill-${o.key}`}
          className={`rounded-full px-3.5 py-2 text-[13px] font-bold transition-colors ${value === o.key ? "bg-terracotta text-white" : "bg-black/[0.04] text-charcoal/60 hover:bg-black/[0.07]"}`}>
          {o.label}{o.count != null ? ` · ${o.count}` : ""}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center gap-3 py-14 text-center">
      {Icon && <Icon className="h-8 w-8 text-charcoal/25" />}
      <p className="text-sm font-medium text-charcoal/45">{text}</p>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative">
      <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal/35" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId}
        className="w-full rounded-xl border border-black/5 bg-white py-2.5 pl-10 pr-4 text-sm text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:ring-2 focus:ring-terracotta/30" />
    </div>
  );
}

export function PersonRow({ name, sub, right, note, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="flex w-full items-center gap-3 rounded-2xl border border-black/5 bg-white p-4 text-left transition-shadow hover:shadow-md">
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-bold text-charcoal">{name}</span>
        {sub && <span className="mt-0.5 block truncate text-[13px] text-charcoal/55">{sub}</span>}
        {note && <span className="mt-1 block text-[13px] font-medium text-charcoal/70">{note}</span>}
      </span>
      {right && <span className="shrink-0 text-[13px] font-semibold text-charcoal/45">{right}</span>}
      {onClick && <ChevronRight className="h-4 w-4 shrink-0 text-charcoal/30" />}
    </button>
  );
}
